import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useSoundFeedback } from '../components/SoundFeedback';
import Header from '../components/Header';
import DailyWidget from '../components/DailyWidget';
import StreakCard from '../components/StreakCard';
import StreakGraph from '../components/StreakGraph';
import MoodSelector from '../components/MoodSelector';
import TriggerSelector from '../components/TriggerSelector';
import StatCard from '../components/StatCard';
import SupportResources from '../components/SupportResources';
import { getMantra } from '../utils/mantras';

const HomeScreen = ({ navigate }) => {
  const { userData, updateStreak, saveDailyLog, getTodayLog, dailyLogs } = useApp();
  const { playCheckIn, playAchievement } = useSoundFeedback();
  const todayLog = getTodayLog();

  const [dayStatus, setDayStatus] = useState(null); // 'clean' | 'hard' | null
  const [mood, setMood] = useState(null);
  const [triggers, setTriggers] = useState([]);
  const [notes, setNotes] = useState('');
  const [showCelebration, setShowCelebration] = useState(false);
  const [showEncouragement, setShowEncouragement] = useState(false);

  const mantra = getMantra({ streak: userData.streak, todayLog });

  const toggleTrigger = (val) =>
    setTriggers(prev => prev.includes(val) ? prev.filter(t => t !== val) : [...prev, val]);

  const isMilestone = (streak) => [7, 14, 30, 50, 100, 365].includes(streak);

  const handleCheckIn = () => {
    if (!mood || !dayStatus) return;
    const today = new Date().toISOString().split('T')[0];
    const isClean = dayStatus === 'clean';
    saveDailyLog(today, { mood, triggers, notes, isClean });
    updateStreak(isClean);

    if (isClean) {
      const newStreak = userData.streak + 1;
      if (isMilestone(newStreak)) {
        playAchievement();
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 4000);
      } else {
        playCheckIn();
      }
    } else {
      playCheckIn();
      setShowEncouragement(true);
    }
  };

  return (
    <div dir="rtl" className="min-h-screen bg-stone-50 pb-24">
      <Header title="יומן גמילה" subtitle="נתיב ההחלמה שלך" />

      {/* Milestone Celebration */}
      {showCelebration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-8 text-center shadow-2xl animate-bounce max-w-sm">
            <div className="text-6xl mb-4">🏆</div>
            <h2 className="text-2xl font-bold text-stone-800 mb-2 font-display">וואו! מדהים!</h2>
            <p className="text-brand-600 font-semibold text-lg">
              {userData.streak} ימים נקיים!
            </p>
            <p className="text-stone-500 text-sm mt-2">עמדה חדשה הושגה! 🌟</p>
          </div>
        </div>
      )}

      {/* Compassionate encouragement after a hard day - calm, not celebratory,
          and requires a deliberate tap to close rather than auto-vanishing. */}
      {showEncouragement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-8 text-center shadow-2xl max-w-sm">
            <div className="text-6xl mb-4">🕊️</div>
            <h2 className="text-2xl font-bold text-stone-800 mb-2 font-display">זה בסדר</h2>
            <p className="text-bloom-600 font-semibold text-lg">
              תיעדת את היום בכנות - וזה צעד חשוב
            </p>
            <p className="text-stone-500 text-sm mt-2">
              הרצף הטוב ביותר שלך ({userData.bestStreak} ימים) עדיין שייך לך. מחר הוא התחלה חדשה 💜
            </p>
            <div className="mt-4">
              <SupportResources compact />
            </div>
            <button
              onClick={() => setShowEncouragement(false)}
              className="mt-4 w-full bg-bloom-500 text-white py-3 rounded-xl font-bold hover:bg-bloom-600 transition"
            >
              המשך
            </button>
          </div>
        </div>
      )}

      <div className="px-4 pt-4 space-y-4">
        <DailyWidget />
        <StreakCard streak={userData.streak} bestStreak={userData.bestStreak} totalDays={userData.totalDays} />
        <StreakGraph logs={dailyLogs} />

        <div>
          <h3 className="font-bold text-stone-800 mb-3 text-lg">🎯 בדיקה יומית</h3>

          {todayLog ? (
            <div className={`border rounded-xl p-6 text-center ${
              todayLog.isClean ? 'bg-brand-50 border-brand-200' : 'bg-bloom-50 border-bloom-200'
            }`}>
              <div className="text-4xl mb-2">{todayLog.isClean ? '✅' : '🤍'}</div>
              <p className={`font-bold text-lg ${todayLog.isClean ? 'text-brand-700' : 'text-bloom-700'}`}>
                {todayLog.isClean ? 'הבדיקה היומית בוצעה!' : 'תיעדת את היום - תודה שהיית כן'}
              </p>
              <p className={`text-sm mt-1 ${todayLog.isClean ? 'text-brand-600' : 'text-bloom-600'}`}>
                {todayLog.isClean ? 'כל הכבוד, המשך כך 💪' : 'מחר הוא הזדמנות חדשה 💜'}
              </p>
            </div>
          ) : !dayStatus ? (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-stone-700">איך היה היום שלך?</p>
              <button
                onClick={() => setDayStatus('clean')}
                className="w-full bg-white border-2 border-brand-200 hover:border-brand-400 hover:bg-brand-50 rounded-xl p-4 flex items-center gap-3 transition-all"
              >
                <span className="text-3xl">✅</span>
                <div className="text-right">
                  <p className="font-bold text-stone-800">נשארתי נקי היום</p>
                  <p className="text-xs text-stone-500">כל יום נקי נספר</p>
                </div>
              </button>
              <button
                onClick={() => setDayStatus('hard')}
                className="w-full bg-white border-2 border-bloom-200 hover:border-bloom-400 hover:bg-bloom-50 rounded-xl p-4 flex items-center gap-3 transition-all"
              >
                <span className="text-3xl">🤍</span>
                <div className="text-right">
                  <p className="font-bold text-stone-800">היה לי יום קשה היום</p>
                  <p className="text-xs text-stone-500">זה בסדר. בוא נתעד את זה ביחד</p>
                </div>
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <button
                onClick={() => setDayStatus(null)}
                className="text-xs font-semibold text-stone-400 hover:text-brand-500 transition"
              >
                ← שנה בחירה
              </button>
              <MoodSelector selected={mood} onSelect={setMood} />
              <TriggerSelector selected={triggers} onToggle={toggleTrigger} />
              <div className="bg-white rounded-xl p-4 border border-stone-200">
                <p className="text-sm font-semibold text-stone-700 mb-2">📝 הערה קצרה</p>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="כתוב משהו על היום שלך..."
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-400"
                  rows={3}
                />
              </div>
              <button
                onClick={handleCheckIn}
                disabled={!mood}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all shadow ${
                  !mood ? 'bg-stone-200 text-stone-400 cursor-not-allowed' :
                  dayStatus === 'clean' ? 'bg-brand-500 text-white hover:bg-brand-600 active:scale-95' :
                  'bg-bloom-500 text-white hover:bg-bloom-600 active:scale-95'
                }`}
              >
                {dayStatus === 'clean' ? 'שמור בדיקה יומית ✓' : 'שמור ותמשיך קדימה 💪'}
              </button>
            </div>
          )}
        </div>

        <div>
          <h3 className="font-bold text-stone-800 mb-3">📊 תובנות</h3>
          <div className="grid grid-cols-3 gap-3">
            <StatCard value={userData.totalDays} label="ימים כוללים" color="text-brand-500" />
            <StatCard value={userData.bestStreak} label="הרצף הטוב" color="text-gold-500" emoji="🏆" />
            <StatCard value={userData.achievements?.length || 0} label="הישגים" color="text-bloom-500" emoji="⭐" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate?.('calendar')}
            className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm text-center hover:bg-stone-50 active:scale-95 transition-all"
          >
            <div className="text-2xl mb-1">📅</div>
            <p className="text-sm font-bold text-stone-800">לוח שנה</p>
            <p className="text-xs text-stone-500">תצוגה חודשית</p>
          </button>
          <button
            onClick={() => navigate?.('stats')}
            className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm text-center hover:bg-stone-50 active:scale-95 transition-all"
          >
            <div className="text-2xl mb-1">📈</div>
            <p className="text-sm font-bold text-stone-800">סטטיסטיקה</p>
            <p className="text-xs text-stone-500">ניתוח מעמיק</p>
          </button>
        </div>

        <div className="bg-gradient-to-r from-bloom-50 to-gold-50 rounded-xl p-4 border border-bloom-100 text-center">
          <p className="text-sm font-semibold text-stone-700 font-display leading-relaxed">{mantra}</p>
        </div>

        <SupportResources />
      </div>
    </div>
  );
};

export default HomeScreen;
